'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/app/Components/AuthProvider';
import { loadUserAcademicData } from '@/app/services/userAcademicData';
import Modal from '@/app/Components/Modal';
import { 
  AcademicCapIcon, 
  ClipboardDocumentCheckIcon,
  ArrowRightIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

interface PrerequisiteCheckerPromptProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PrerequisiteCheckerPrompt({ isOpen, onClose }: PrerequisiteCheckerPromptProps) {
  const { user } = useAuth();
  const router = useRouter();
  const [hasCompletedCourses, setHasCompletedCourses] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkUserCourses = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }

      try {
        const academicData = await loadUserAcademicData(user.id);
        const hasCourses = academicData?.completedCourses && academicData.completedCourses.length > 0;
        setHasCompletedCourses(hasCourses || false);
      } catch (error) {
        console.error('Error checking user courses:', error);
        setHasCompletedCourses(false);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      checkUserCourses();
    }
  }, [isOpen, user?.id]);

  const handleGoToPrerequisiteChecker = () => {
    router.push('/prerequisite-checker');
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  // Don't show the prompt if user has completed courses or is still loading
  if (isLoading || hasCompletedCourses === true) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" showCloseButton={false}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <ClipboardDocumentCheckIcon className="h-8 w-8 text-red-500 mr-3" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Complete Your Academic Profile
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors p-1"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            To get the most accurate degree planning and search results, please add your previously completed courses to your academic profile.
          </p>
          
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-control p-4">
            <div className="flex items-start">
              <AcademicCapIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                  Why is this important?
                </h4>
                <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                  <li>• Get accurate prerequisite checking for courses</li>
                  <li>• See which degree requirements you've already satisfied</li>
                  <li>• Receive personalized course recommendations</li>
                  <li>• Track your progress toward graduation</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleGoToPrerequisiteChecker}
            className="flex-1 bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-control transition-colors flex items-center justify-center"
          >
            <ClipboardDocumentCheckIcon className="h-5 w-5 mr-2" />
            Add My Courses
            <ArrowRightIcon className="h-4 w-4 ml-2" />
          </button>
          <button
            onClick={handleClose}
            className="flex-1 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 font-medium py-2 px-4 rounded-control transition-colors"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </Modal>
  );
}
